import { useEffect, useMemo, useState } from 'react';
import './Admin.css';

// SQLite datetime('now') stocke "YYYY-MM-DD HH:MM:SS" en UTC → affichage local "JJ.MM.AAAA HH:MM"
function formatDate(value) {
  if (!value) return '';
  const date = new Date(`${value.replace(' ', 'T')}Z`);
  if (Number.isNaN(date.getTime())) return value;
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function AdminLogin({ onLoggedIn }) {
  const [password, setPassword] = useState('');
  const [erreur, setErreur] = useState('');
  const [envoiEnCours, setEnvoiEnCours] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErreur('');
    setEnvoiEnCours(true);

    try {
      const response = await fetch('/api/admin/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Connexion impossible');
      }

      onLoggedIn();
    } catch (error) {
      setErreur(error.message);
    } finally {
      setEnvoiEnCours(false);
    }
  };

  return (
    <div className="section container admin-login">
      <h1 className="section-title">Administration</h1>
      <form className="admin-login-form card" onSubmit={handleSubmit}>
        <label>
          Mot de passe
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoFocus
          />
        </label>
        {erreur && <p className="admin-erreur">{erreur}</p>}
        <button type="submit" className="btn btn-primary" disabled={envoiEnCours}>
          {envoiEnCours ? 'Connexion...' : 'Se connecter'}
        </button>
      </form>
    </div>
  );
}

function GuestsSection({ guests, onChange }) {
  const [prenom, setPrenom] = useState('');
  const [nom, setNom] = useState('');
  const [bulkTexte, setBulkTexte] = useState('');
  const [erreur, setErreur] = useState('');
  const [confirmingId, setConfirmingId] = useState(null);
  const [tri, setTri] = useState({ colonne: null, direction: 'asc' });

  const handleTri = (colonne) => {
    setTri((prev) =>
      prev.colonne === colonne
        ? { colonne, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { colonne, direction: 'asc' }
    );
  };

  const guestsTries = useMemo(() => {
    if (!tri.colonne) return guests;

    const compares = [...guests].sort((a, b) => {
      const valA = a[tri.colonne];
      const valB = b[tri.colonne];

      if (valA == null && valB == null) return 0;
      if (valA == null) return 1;
      if (valB == null) return -1;

      if (typeof valA === 'number' && typeof valB === 'number') {
        return valA - valB;
      }

      return String(valA).localeCompare(String(valB), 'fr', { sensitivity: 'base' });
    });

    return tri.direction === 'asc' ? compares : compares.reverse();
  }, [guests, tri]);

  const renderTh = (label, colonne) => (
    <th className="admin-th-sortable" onClick={() => handleTri(colonne)}>
      {label}
      {tri.colonne === colonne && (
        <span className="admin-tri-icone">{tri.direction === 'asc' ? ' ▲' : ' ▼'}</span>
      )}
    </th>
  );

  const handleAdd = async (event) => {
    event.preventDefault();
    setErreur('');

    try {
      const response = await fetch('/api/admin/guests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prenom, nom }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || "Impossible d'ajouter l'invité.");
      }

      setPrenom('');
      setNom('');
      onChange();
    } catch (error) {
      setErreur(error.message);
    }
  };

  const handleBulkAdd = async (event) => {
    event.preventDefault();
    setErreur('');

    try {
      const response = await fetch('/api/admin/guests/bulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ texte: bulkTexte }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || "Impossible d'ajouter la liste.");
      }

      setBulkTexte('');
      onChange();
    } catch (error) {
      setErreur(error.message);
    }
  };

  const handleDelete = async (id) => {
    if (confirmingId !== id) {
      setConfirmingId(id);
      return;
    }

    setConfirmingId(null);
    await fetch(`/api/admin/guests/${id}`, { method: 'DELETE' });
    onChange();
  };

  return (
    <section className="admin-section">
      <div className="admin-section-header">
        <h2>Liste des invités ({guests.length})</h2>
      </div>

      {erreur && <p className="admin-erreur">{erreur}</p>}

      <div className="admin-guests-forms">
        <form className="admin-guest-form card" onSubmit={handleAdd}>
          <h3>Ajouter un invité</h3>
          <label>
            Prénom
            <input type="text" value={prenom} onChange={(e) => setPrenom(e.target.value)} required />
          </label>
          <label>
            Nom
            <input type="text" value={nom} onChange={(e) => setNom(e.target.value)} required />
          </label>
          <button type="submit" className="btn btn-primary">
            Ajouter
          </button>
        </form>

        <form className="admin-guest-form card" onSubmit={handleBulkAdd}>
          <h3>Ajout groupé</h3>
          <label>
            Une ligne par invité&nbsp;: Prénom, Nom
            <textarea
              rows={6}
              value={bulkTexte}
              onChange={(e) => setBulkTexte(e.target.value)}
              placeholder={'Camille, Martin\nLucas, Dupuis'}
            />
          </label>
          <button type="submit" className="btn btn-primary">
            Ajouter la liste
          </button>
        </form>
      </div>

      <div className="admin-table-wrapper">
        <table className="admin-table">
          <thead>
            <tr>
              {renderTh('Prénom', 'prenom')}
              {renderTh('Nom', 'nom')}
              {renderTh('Statut', 'presence')}
              {renderTh('Répondu le', 'repondu_le')}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {guestsTries.map((guest) => (
              <tr key={guest.id}>
                <td>{guest.prenom}</td>
                <td>{guest.nom}</td>
                <td>
                  {guest.presence === 'oui'
                    ? 'Vient'
                    : guest.presence === 'non'
                      ? 'Ne vient pas'
                      : 'En attente'}
                </td>
                <td>{formatDate(guest.repondu_le)}</td>
                <td>
                  <button
                    type="button"
                    className="admin-delete-btn"
                    onClick={() => handleDelete(guest.id)}
                  >
                    {confirmingId === guest.id ? 'Confirmer' : 'Supprimer'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function AdminDashboard({ onLoggedOut }) {
  const [rsvps, setRsvps] = useState([]);
  const [chansons, setChansons] = useState([]);
  const [guests, setGuests] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [confirmingChansonId, setConfirmingChansonId] = useState(null);

  const charger = async () => {
    try {
      const [rsvpRes, musiqueRes, guestsRes] = await Promise.all([
        fetch('/api/admin/rsvp'),
        fetch('/api/admin/musique'),
        fetch('/api/admin/guests'),
      ]);

      if (!rsvpRes.ok || !musiqueRes.ok || !guestsRes.ok) {
        throw new Error();
      }

      const rsvpData = await rsvpRes.json();
      const musiqueData = await musiqueRes.json();
      const guestsData = await guestsRes.json();
      setRsvps(rsvpData.rows || []);
      setChansons(musiqueData.rows || []);
      setGuests(guestsData.rows || []);
    } catch {
      setErreur('Impossible de charger les données.');
    } finally {
      setChargement(false);
    }
  };

  useEffect(() => {
    charger();
  }, []);

  const handleLogout = async () => {
    await fetch('/api/admin/logout', { method: 'POST' });
    onLoggedOut();
  };

  const handleDeleteChanson = async (id) => {
    if (confirmingChansonId !== id) {
      setConfirmingChansonId(id);
      return;
    }

    setConfirmingChansonId(null);
    await fetch(`/api/admin/musique/${id}`, { method: 'DELETE' });
    charger();
  };

  const totalPersonnes = rsvps.filter((r) => r.presence === 'oui').length;

  return (
    <div className="section container admin-dashboard">
      <div className="admin-header">
        <h1 className="section-title">Administration</h1>
        <button className="btn btn-secondary" onClick={handleLogout}>
          Se déconnecter
        </button>
      </div>

      {chargement && <p>Chargement...</p>}
      {erreur && <p className="admin-erreur">{erreur}</p>}

      {!chargement && !erreur && (
        <>
          <GuestsSection guests={guests} onChange={charger} />

          <section className="admin-section">
            <div className="admin-section-header">
              <h2>
                RSVP ({rsvps.length} réponse{rsvps.length > 1 ? 's' : ''}, {totalPersonnes}{' '}
                personne{totalPersonnes > 1 ? 's' : ''} confirmée{totalPersonnes > 1 ? 's' : ''})
              </h2>
              <a className="btn btn-secondary" href="/api/admin/export/rsvp.csv">
                Exporter en CSV
              </a>
            </div>
            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Prénom</th>
                    <th>Nom</th>
                    <th>Email</th>
                    <th>Présence</th>
                    <th>Régime</th>
                    <th>Message</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {rsvps.map((row) => (
                    <tr key={row.id}>
                      <td>{row.prenom}</td>
                      <td>{row.nom}</td>
                      <td>{row.email}</td>
                      <td>{row.presence === 'oui' ? 'Oui' : 'Non'}</td>
                      <td>{row.regime}</td>
                      <td>{row.message}</td>
                      <td>{formatDate(row.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="admin-section">
            <div className="admin-section-header">
              <h2>
                Musique ({chansons.length} suggestion{chansons.length > 1 ? 's' : ''})
              </h2>
              <a className="btn btn-secondary" href="/api/admin/export/musique.csv">
                Exporter en CSV
              </a>
            </div>
            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Prénom</th>
                    <th>Titre &amp; artiste</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {chansons.map((row) => (
                    <tr key={row.id}>
                      <td>{row.nom}</td>
                      <td>{row.chanson}</td>
                      <td>
                        <button
                          type="button"
                          className="admin-delete-btn"
                          onClick={() => handleDeleteChanson(row.id)}
                        >
                          {confirmingChansonId === row.id ? 'Confirmer' : 'Supprimer'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function Admin() {
  const [isAdmin, setIsAdmin] = useState(false);
  const [verification, setVerification] = useState(true);

  useEffect(() => {
    fetch('/api/admin/me')
      .then((res) => res.json())
      .then((data) => setIsAdmin(!!data.isAdmin))
      .catch(() => setIsAdmin(false))
      .finally(() => setVerification(false));
  }, []);

  if (verification) {
    return (
      <div className="section container">
        <p>Chargement...</p>
      </div>
    );
  }

  return isAdmin ? (
    <AdminDashboard onLoggedOut={() => setIsAdmin(false)} />
  ) : (
    <AdminLogin onLoggedIn={() => setIsAdmin(true)} />
  );
}

export default Admin;
